import { useCallback, useEffect, useMemo, useState } from 'react';
import { FiCopy, FiRefreshCcw } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Button } from '~/components/shadcn/button-simple';
import { Card, CardContent } from '~/components/shadcn/card-simple';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/shadcn/table-simple';
import { InventoryLocationLog, LocationListProps, LocationUnitListResponse } from '~/pages/Location/LocationInventory/types';
import api from '~/services/api';
import copyTextToClipboard from '~/utils/copyTextToClipboard';

function formatDate(value: string) {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

export function LocationInventory({ match }: LocationListProps) {
  const locationId = match.params.location_id;

  const [inventory, setInventory] = useState<InventoryLocationLog[]>([]);
  const [quantity, setQuantity] = useState(0);
  const [locationName, setLocationName] = useState('');
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const headerTitle = useMemo(() => {
    if (locationName) return `Location Inventory - ${locationName}`;
    return 'Location Inventory';
  }, [locationName]);

  const fetchLocationInventory = useCallback(async () => {
    if (!locationId) {
      setInventory([]);
      setQuantity(0);
      setLocationName('');
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const response = await api.get<LocationUnitListResponse>(`/locations/getLocationUnitList/${locationId}`);

      setInventory(response.data?.inventoryLocationLog || []);
      setQuantity(Number(response.data?.quantity ?? 0));
      setLocationName(response.data?.locationName?.description || '');
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to load location inventory.';
      setErrorMessage(message);
      toast.error(message);
      setInventory([]);
      setQuantity(0);
      setLocationName('');
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    fetchLocationInventory();
  }, [fetchLocationInventory]);

  const handleCopySerial = useCallback(async (serialNumber: string) => {
    const copied = await copyTextToClipboard(serialNumber);

    if (copied) {
      toast.success('Serial number copied');
      return;
    }

    toast.error('Unable to copy serial number');
  }, []);

  return (
    <div className="min-h-screen bg-white text-foreground lg:pl-64">
      <main className="space-y-5 px-4 pb-8 pt-20 lg:px-2 lg:pb-10">
        <section className="rounded-xl border border-border/80 bg-background/95 p-4 shadow-sm backdrop-blur-sm lg:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col gap-2">
              <Link to="/location" className="inline-flex">
                <Button variant="outline" className="h-8 w-fit gap-2 border-border/70 px-3 text-xs" type="button">
                  Back
                </Button>
              </Link>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-xl font-semibold tracking-tight lg:text-2xl">{headerTitle}</h1>
                <span className="inline-flex items-center rounded-full border border-border/70 bg-muted/40 px-3 py-1 text-xs font-medium text-foreground">
                  Total: {quantity}
                </span>
              </div>
            </div>

            <Button type="button" variant="outline" size="sm" onClick={fetchLocationInventory} className="h-9 gap-2" disabled={loading}>
              <FiRefreshCcw className="h-4 w-4" />
              Refresh
            </Button>
          </div>
        </section>

        <Card className="border-border/80 shadow-sm">
          <CardContent className="p-0">
            {loading && (
              <div className="space-y-3 p-4">
                <div className="h-10 animate-pulse rounded-md bg-muted" />
                <div className="h-10 animate-pulse rounded-md bg-muted" />
                <div className="h-10 animate-pulse rounded-md bg-muted" />
                <div className="h-10 animate-pulse rounded-md bg-muted" />
              </div>
            )}

            {!loading && errorMessage && (
              <div className="flex flex-col items-center justify-center gap-3 p-8 text-center">
                <p className="text-sm text-destructive">{errorMessage}</p>
                <Button type="button" variant="outline" onClick={fetchLocationInventory} className="gap-2">
                  <FiRefreshCcw className="h-4 w-4" />
                  Retry
                </Button>
              </div>
            )}

            {!loading && !errorMessage && inventory.length === 0 && (
              <div className="p-8 text-center text-sm text-muted-foreground">No inventory found for this location.</div>
            )}

            {!loading && !errorMessage && inventory.length > 0 && (
              <div className="max-h-[calc(100vh-20rem)] overflow-auto">
                <Table className="min-w-[1200px]">
                  <TableHeader className="sticky top-0 z-10 border-b bg-muted/50 backdrop-blur-sm">
                    <TableRow>
                      <TableHead className="whitespace-nowrap">Serial Number</TableHead>
                      <TableHead className="whitespace-nowrap">Part Number</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="whitespace-nowrap">Status</TableHead>
                      <TableHead className="whitespace-nowrap">Grade</TableHead>
                      <TableHead className="whitespace-nowrap">Previous Location</TableHead>
                      <TableHead className="whitespace-nowrap">Previous Location Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inventory.map((row) => (
                      <TableRow key={row.id} className="border-b border-border/60 transition-colors hover:bg-muted/40">
                        <TableCell className="align-top">
                          <div className="flex items-center gap-2">
                            <a
                              href={`/inventoryProcessing/${row.id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary underline-offset-4 hover:underline"
                            >
                              {row.serial_number}
                            </a>
                            <button
                              type="button"
                              onClick={() => handleCopySerial(row.serial_number)}
                              aria-label={`Copy serial number ${row.serial_number}`}
                              title="Copy serial number"
                              className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            >
                              <FiCopy className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </TableCell>
                        <TableCell className="align-top font-medium">{row.item?.part_number || '-'}</TableCell>
                        <TableCell className="max-w-[420px] align-top">
                          <div className="line-clamp-2" title={row.item?.description || ''}>
                            {row.item?.description || '-'}
                          </div>
                        </TableCell>
                        <TableCell className="align-top">{row.status || '-'}</TableCell>
                        <TableCell className="align-top">{row.grade || '-'}</TableCell>
                        <TableCell className="align-top">{row.previous_location || '-'}</TableCell>
                        <TableCell className="align-top">{formatDate(row.previous_location_date)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
